import React, { useState, useEffect } from "react";

/*
  Najma Al Khautar — App.jsx
  - Full single-file React component (Tailwind classes used)
  - This file is a complete, runnable React component that exports a valid App() function
  - Fixes the previous error (Minified React error #130) by ensuring a valid component is exported
    and by avoiding rendering raw objects as JSX children.

  Instructions:
  - Paste this file into src/App.jsx of your React project
  - Tailwind is optional but recommended for styling; the component will still render without it
  - Replace /public/images and /public/videos placeholders with your real media files
*/

const PRODUCTS = [
  { id: "rhodes", title: "Premium Rhodes Grass", desc: "10-12% Protein, <12% Moisture. High-quality bales for GCC export." },
  { id: "alfalfa", title: "Alfalfa Hay (Lucerne)", desc: "High-protein forage for dairy cattle and horses. Grade A quality." },
  { id: "sudan", title: "Sudan Grass", desc: "Green, succulent and highly palatable fodder for livestock." },
  { id: "wheat", title: "Wheat Straw Bales", desc: "Clean, dry wheat straw for bedding and roughage." },
];

const TRUST_LOGOS = [
  

];


function ProductCard({ product, onQuote }) {
  // product is an object but we render only its primitive fields (title/desc)
  const whatsappUrl = `https://wa.me/971566229072?text=${encodeURIComponent(`I am interested in buying ${product.title}. Please provide more details.`)}`;
  
  return (
    <div className="bg-white p-5 rounded-2xl shadow hover:shadow-2xl transition transform hover:-translate-y-1">
      <h3 className="font-bold text-lg text-green-900">{product.title}</h3>
      <p className="text-sm text-green-700 mt-2">{product.desc}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => onQuote(product.title)} className="px-3 py-2 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 flex-1">Quote</button>
        <a href={whatsappUrl} target="_blank" rel="noreferrer" className="px-3 py-2 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 flex-1 text-center">WhatsApp</a>
        <a href={`mailto:najmaalkauthar@gmail.com?subject=Inquiry about ${encodeURIComponent(product.title)}`} className="px-3 py-2 border rounded-lg text-xs">Email</a>
      </div>
    </div>
  );
}

function Counter({ end, label }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let start = 0;
    if (!Number.isFinite(end) || end <= 0) return;
    const duration = 1400; // ms
    const stepTime = Math.max(10, Math.floor(duration / end));
    const timer = setInterval(() => {
      start += 1;
      setValue(start);
      if (start >= end) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [end]);

  return (
    <div className="text-center p-4">
      <div className="text-3xl md:text-4xl font-extrabold text-green-900">{value}{end >= 100 ? "+" : ""}</div>
      <div className="text-sm text-green-700">{label}</div>
    </div>
  );
}

export default function App() {
  // App state
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState("");
  const [newsletterMsg, setNewsletterMsg] = useState("");

  useEffect(() => {
    // set a helpful title and meta description for SEO (optional)
    document.title = "Najma Al Khautar — Rhodes Grass Exporter to UAE, Saudi Arabia, Qatar & Oman";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = "Najma Al Khautar supplies premium Rhodes Grass, wheat, corn and tailored feed mixes. We are leading exporters to UAE (Dubai, Abu Dhabi), Saudi Arabia (Riyadh, Jeddah), Qatar (Doha), Oman (Muscat), Kuwait and Bahrain.";
  }, []);

  function openQuote(productTitle) {
    setQuoteProduct(productTitle || "General Inquiry");
    setQuoteOpen(true);
  }
  function closeQuote() {
    setQuoteOpen(false);
    setQuoteProduct("");
  }

  async function subscribeNewsletter(e) {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    // Basic network submit to Formspree endpoint
    try {
      const res = await fetch("https://formspree.io/f/xdkwebkn", { method: "POST", body: data });
      if (res.ok) setNewsletterMsg("✅ Subscribed — thank you!");
      else setNewsletterMsg("❌ Subscription failed. Try again later.");
    } catch (err) {
      setNewsletterMsg("❌ Network error.");
    }
    setTimeout(() => setNewsletterMsg(""), 4000);
    form.reset();
  }

  return (
    <div className="min-h-screen bg-fixed text-gray-900 p-6" style={{
      backgroundImage: "linear-gradient(180deg, rgba(242,253,245,0.95), rgba(225,249,235,0.9)), url('/images/logo512.webp')",
      backgroundBlendMode: 'overlay',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>

      {/* Floating WhatsApp + Call */}
      <div className="fixed right-6 bottom-6 flex flex-col gap-3 z-50">
        <a href="https://wa.me/971566229072" target="_blank" rel="noreferrer" className="w-14 h-14 rounded-full flex items-center justify-center bg-green-600 text-white shadow-xl hover:scale-105 transform transition" aria-label="WhatsApp">💬</a>
        <a href="tel:+971566229072" className="w-14 h-14 rounded-full flex items-center justify-center bg-amber-500 text-white shadow-xl hover:scale-105 transform transition" aria-label="Call">📞</a>
      </div>

      {/* Header */}
      <header className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          {/* replace the NK box with your logo */}
          <img src="\logo512.webp" alt="Najma Al Khautar - Rhodes Grass Exporter logo" className="w-14 h-14 rounded-2xl object-cover shadow-md" />
          <div>
            <h1 className="sr-only">Najma Al Khautar - Premium Rhodes Grass Exporter to Gulf Region</h1>
            <h2 className="text-2xl font-extrabold">Najma Al Khautar</h2>
            <p className="text-sm text-gray-700">Premium Rhodes Grass & Animal Feed — Export Ready</p>
          </div>
        </div>
        <nav className="flex items-center gap-4">
          <a href="#products" className="text-sm font-semibold hover:underline">Products</a>
          <a href="#why" className="text-sm font-semibold hover:underline">Why Us</a>
          <a href="#testimonials" className="text-sm font-semibold hover:underline">Testimonials</a>
          <div id="google_translate_element" className="scale-90 origin-right"></div>
          <a href="https://wa.me/971566229072" target="_blank" rel="noreferrer" className="hidden md:flex px-4 py-2 bg-emerald-500 text-white rounded-lg font-bold shadow hover:bg-emerald-600 items-center gap-2"><span>Order via WhatsApp</span></a>
          <button className="hidden lg:block px-4 py-2 border border-green-700 text-green-700 rounded-lg font-bold hover:bg-green-50">Download Catalog (PDF)</button>
          <button onClick={() => openQuote('General Inquiry')} className="px-4 py-2 bg-green-700 text-white rounded-lg font-bold shadow hover:bg-green-800">Get Quote</button>
        </nav>
      </header>







      {/* Hero */}
      <section className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center mb-10">
        <div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-green-900 leading-tight">Fresh. Green. Trusted — Najma Al Khautar</h2>
          <p className="mt-4 text-lg text-green-800">Export-quality Rhodes Grass bales, wheat, corn and tailored feed mixes — backed with certificates and fast logistics. Everyone chooses us once — and never looks back.</p>
          <div className="mt-6 flex gap-4">
            <button onClick={() => openQuote('Rhodes Grass')} className="px-6 py-3 bg-green-700 text-white rounded-lg font-bold shadow hover:bg-green-800">Request Quote</button>
            <a href="#contact" className="px-6 py-3 border border-green-700 rounded-lg font-bold hover:bg-green-100">Contact</a>
          </div>
          <div className="mt-6 flex gap-6 items-center text-sm text-green-900">
            <div>📞 +971524050997</div>
            <div>✉️ najmaalkauthar@gmail.com</div>
          </div>

          {/* Trust points */}
          <div className="mt-8 flex flex-wrap gap-4 items-center justify-center md:justify-start text-xs font-bold text-green-800 uppercase tracking-widest">
            <span className="flex items-center gap-1">🛡️ SGS Certified</span>
            <span className="flex items-center gap-1">🌿 100% Organic</span>
            <span className="flex items-center gap-1">📦 Bulk Export Ready</span>
            <span className="flex items-center gap-1">✅ Quality Guaranteed</span>
          </div>

          {/* Arabic Welcome Section */}
          <div className="mt-8 bg-green-50/80 p-6 rounded-2xl border-r-4 border-green-700 text-right shadow-sm" dir="rtl">
            <h3 className="text-xl font-bold text-green-900 mb-2 font-serif">مرحباً بكم في نجمة الكوثر لتصدير الأعلاف</h3>
            <p className="text-sm text-green-800 leading-relaxed">
              نحن متخصصون في تصدير **عشب الرودس الممتاز** والبرسيم الحجازي (Alfalfa) عالي البروتين إلى جميع دول الخليج. نضمن لكم الجودة العالية، وشهادات الفحص المعتمدة، وسرعة التوصيل إلى الإمارات، السعودية، قطر، عمان، الكويت، والبحرين.
            </p>
            <div className="mt-4 flex justify-start">
              <a href="https://wa.me/971566229072" className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition shadow">
                <span>اطلب الآن عبر الواتساب</span>
                <span className="text-xl">💬</span>
              </a>
            </div>
          </div>
        </div>


        <div>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <video className="w-full h-auto" autoPlay muted loop playsInline>
              <source src="/videos/rhode-grass-video.mp4.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className="mt-4 text-sm text-gray-600">Quality checked · Export documentation provided · Flexible shipping</div>
        </div>
      </section>

{/* About Section */}
<section id="about" className="max-w-7xl mx-auto mb-12">
  <div className="bg-white/90 p-8 rounded-2xl shadow-md">
    <h3 className="text-3xl font-extrabold text-green-900 mb-4">About Najma Al Kauthar</h3>
    <p className="text-green-800 text-lg mb-4">
      Najma Al Kauthar is a trusted name in the export of premium Rhodes Grass, wheatstraw, corn, and animal feed across the **Gulf Cooperation Council (GCC)** and Africa. With 10 years of experience, we specialize in supplying high-quality fodder to **United Arab Emirates (UAE), Saudi Arabia (KSA), Oman, Qatar, Kuwait, and Bahrain**.
    </p>
    <p className="text-green-800 text-lg">
      Our mission is simple: deliver high-quality, natural feed with honesty, transparency, and a commitment to long-term partnerships. Whether you're a distributor in **Dubai, Riyadh, Doha, or Muscat** — we're here to support your success with export-ready livestock nutrition.
    </p>
  </div>
</section>

      {/* Why Choose Us */}
      <section id="why" className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 mb-10">
        <div className="space-y-4">
          <h3 className="text-3xl font-extrabold text-green-900">Why Choose Najma Al Khautar</h3>
          <p className="text-green-800">Because every farmer and buyer deserves a partner they can trust. We don’t just supply feed — we deliver confidence. Below are the reasons almost everyone chooses us as their first and final choice.</p>

          <ul className="mt-4 space-y-3">
            <li className="bg-white/90 p-4 rounded-2xl shadow">✅ <strong>Premium Rhodes Grass (10-12% Protein)</strong> — High nutritional value with low moisture content (less than 12%), perfect for horses and cattle.</li>
            <li className="bg-white/90 p-4 rounded-2xl shadow">✅ <strong>UAE & Saudi Export Specialists</strong> — We handle all SGS certificates, phytosanitary documents, and customs clearance for Dubai, Riyadh, and Jeddah.</li>
            <li className="bg-white/90 p-4 rounded-2xl shadow">✅ <strong>Bulk Supply & Reliable Logistics</strong> — Capacity to export over 5,000 tons monthly via Sea (Jebel Ali, Dammam) or Land transport.</li>
            <li className="bg-white/90 p-4 rounded-2xl shadow">✅ <strong>Direct Farm Pricing</strong> — No middlemen. Get the most competitive rates for high-quality Rhodes hay and Alfalfa.</li>
          </ul>

          <blockquote className="mt-4 p-4 bg-emerald-50 rounded-lg border-l-4 border-green-700">“Everyone chooses us once — and never looks back.”</blockquote>

          <div className="mt-6">
            <button onClick={() => openQuote('Partnership')} className="px-5 py-3 bg-amber-500 text-white rounded-lg font-bold shadow hover:bg-amber-600">Partner With Us</button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white/90 p-6 rounded-2xl shadow-md text-center">
            <div className="text-4xl mb-3">🌱</div>
            <h4 className="font-bold text-lg text-green-900">Quality Produce</h4>
            <p className="text-sm text-green-700 mt-2">Sustainably harvested and tested.</p>
          </div>

          <div className="bg-white/90 p-6 rounded-2xl shadow-md text-center">
            <div className="text-4xl mb-3">🚚</div>
            <h4 className="font-bold text-lg text-green-900">Fast Delivery</h4>
            <p className="text-sm text-green-700 mt-2">Sea, air and land — insured shipping.</p>
          </div>

          <div className="bg-white/90 p-6 rounded-2xl shadow-md text-center">
            <div className="text-4xl mb-3">🌍</div>
            <h4 className="font-bold text-lg text-green-900">Global Reach</h4>
            <p className="text-sm text-green-700 mt-2">Export compliance and trade experience.</p>
          </div>

          <div className="bg-white/90 p-6 rounded-2xl shadow-md text-center">
            <div className="text-4xl mb-3">💰</div>
            <h4 className="font-bold text-lg text-green-900">Competitive Pricing</h4>
            <p className="text-sm text-green-700 mt-2">Transparent quotes and flexible contracts.</p>
          </div>
        </div>
      </section>

      {/* Counters */}
      <section className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6 mb-10 bg-white/70 p-6 rounded-2xl shadow-md">
        <Counter end={10} label={'Years Experience'} />
        <Counter end={30000} label={'Tons Exported'} />
        <Counter end={15} label={'Countries Served'} />
      </section>

      {/* Markets Section */}
      <section id="markets" className="max-w-7xl mx-auto mb-10 text-center">
        <h3 className="text-3xl font-extrabold text-green-900 mb-6">Our Gulf Presence</h3>
        <p className="text-green-800 mb-8 max-w-2xl mx-auto">We provide fast shipping and export documentation for Rhodes Grass and Animal Feed to all GCC countries.</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="bg-white/80 p-4 rounded-xl shadow border border-green-100 flex flex-col items-center hover:bg-green-50 transition">
            <span className="text-2xl mb-1">🇦🇪</span>
            <span className="font-bold text-green-900">UAE Exporter</span>
            <span className="text-xs text-green-700">Dubai & Abu Dhabi</span>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-green-100 flex flex-col items-center hover:bg-green-50 transition">
            <span className="text-2xl mb-1">🇸🇦</span>
            <span className="font-bold text-green-900">KSA Exporter</span>
            <span className="text-xs text-green-700">Riyadh & Jeddah</span>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-green-100 flex flex-col items-center hover:bg-green-50 transition">
            <span className="text-2xl mb-1">🇴🇲</span>
            <span className="font-bold text-green-900">Oman Supplier</span>
            <span className="text-xs text-green-700">Muscat & Salalah</span>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-green-100 flex flex-col items-center hover:bg-green-50 transition">
            <span className="text-2xl mb-1">🇶🇦</span>
            <span className="font-bold text-green-900">Qatar Exporter</span>
            <span className="text-xs text-green-700">Doha Market</span>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-green-100 flex flex-col items-center hover:bg-green-50 transition">
            <span className="text-2xl mb-1">🇰🇼</span>
            <span className="font-bold text-green-900">Kuwait Supplier</span>
            <span className="text-xs text-green-700">Kuwait City</span>
          </div>
          <div className="bg-white/80 p-4 rounded-xl shadow border border-green-100 flex flex-col items-center hover:bg-green-50 transition">
            <span className="text-2xl mb-1">🇧🇭</span>
            <span className="font-bold text-green-900">Bahrain Exporter</span>
            <span className="text-xs text-green-700">Manama Market</span>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="max-w-7xl mx-auto mb-10">
        <h3 className="text-2xl font-bold text-green-900 mb-4 text-center underline decoration-amber-400">Our Premium Export Products</h3>
        <div className="grid md:grid-cols-4 gap-6">
          {PRODUCTS.map((p) => (
            <ProductCard key={p.id} product={p} onQuote={openQuote} />
          ))}
        </div>
      </section>

      {/* Lead Magnet Section (Strategic Placement) */}
      <section className="max-w-7xl mx-auto mb-12 px-6">
        <div className="bg-amber-400 p-8 rounded-3xl shadow-xl relative overflow-hidden border-2 border-green-800/10">
          <div className="md:flex items-center justify-between gap-6 relative z-10">
            <div className="flex-1">
              <h3 className="text-2xl font-black text-green-900 uppercase italic">Limited Time Offer</h3>
              <p className="text-green-800 font-bold text-lg">Download our 2026 Price List & SGS Lab Quality Analysis Reports.</p>
            </div>
            <div className="mt-6 md:mt-0 flex flex-wrap gap-3">
              <button onClick={() => openQuote('2026 Price List')} className="px-8 py-4 bg-green-900 text-white rounded-xl font-bold hover:bg-black transition-all shadow-lg">Download Price List</button>
              <a href="https://wa.me/971566229072?text=I%20want%20to%20see%20the%202026%20Price%20List%20and%20Lab%20Reports" target="_blank" rel="noreferrer" className="px-8 py-4 bg-white text-green-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg flex items-center gap-2 border border-green-200">
                <span>WhatsApp Price Request</span>
              </a>
            </div>
          </div>
          {/* Decorative background circle */}
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/20 rounded-full"></div>
        </div>
      </section>

      {/* Gallery slider (CSS only) */}
      <section id="gallery" className="max-w-7xl mx-auto mb-10">
        <h3 className="text-2xl font-bold text-green-900 mb-4">Our Premium Rhodes Grass Gallery</h3>
        <div className="relative overflow-hidden rounded-2xl shadow-lg">
          <div className="flex w-[300%] animate-slide">
             <img src="/images/Rhode Grass 3.webp" className="w-1/3 object-cover h-64" alt="Premium Green Rhodes Grass Bales for Export" />
             <img src="/images/Rhode Grass 4.webp" className="w-1/3 object-cover h-64" alt="Export Quality Rhodes Hay loaded for shipment" />
          </div>
        </div>
      </section>

      {/* Advanced SEO Booster Section */}
      <section id="insights" className="max-w-7xl mx-auto mb-10 bg-white/80 p-8 rounded-3xl shadow-lg border-2 border-green-50">
        <h3 className="text-3xl font-extrabold text-green-900 mb-6 text-center">Expert Insights: Premium Fodder for Maximum Livestock Yield</h3>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-green-800">Why Rhodes Grass (Chloris gayana)?</h4>
            <p className="text-sm text-green-700 leading-relaxed">Rhodes grass is the backbone of the Gulf livestock industry. With a crude protein content of 10-12% and high digestibility, it is the preferred maintenance fodder for camels, horses, and cattle in UAE and Saudi Arabia. Our Rhodes hay is harvested at the perfect maturity stage to ensure zero dust and maximum palatability.</p>
          </div>
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-green-800">Alfalfa Hay: The Protein Powerhouse</h4>
            <p className="text-sm text-green-700 leading-relaxed">For high-performance dairy farms in Qatar and Oman, our Grade-A Alfalfa (Lucerne) provides the necessary calcium and 18%+ protein levels required for milk production. We ensure a moisture-locked baling process to prevent nutrient loss during long-distance sea shipping.</p>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-green-100 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div><span className="block font-bold text-green-900">100% Organic</span><span className="text-xs text-green-600">No chemicals used</span></div>
          <div><span className="block font-bold text-green-900">SGS Certified</span><span className="text-xs text-green-600">Global Standards</span></div>
          <div><span className="block font-bold text-green-900">Dust-Free</span><span className="text-xs text-green-600">Safe for Horses</span></div>
          <div><span className="block font-bold text-green-900">Fast Shipping</span><span className="text-xs text-green-600">Door-to-Door available</span></div>
        </div>
      </section>

      {/* Technical Specifications Table */}
      <section id="specs" className="max-w-7xl mx-auto mb-10 px-6">
        <h3 className="text-3xl font-extrabold text-green-900 mb-6 text-center">Technical Specifications</h3>
        <div className="overflow-x-auto bg-white rounded-3xl shadow-xl border border-green-100">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-green-800 text-white">
                <th className="p-4 border-b">Nutritional Metric</th>
                <th className="p-4 border-b">Premium Rhodes Grass</th>
                <th className="p-4 border-b">Grade-A Alfalfa Hay</th>
              </tr>
            </thead>
            <tbody className="text-green-900">
              <tr className="hover:bg-green-50 transition">
                <td className="p-4 border-b font-bold">Crude Protein</td>
                <td className="p-4 border-b">10% - 12%</td>
                <td className="p-4 border-b">18% - 22%</td>
              </tr>
              <tr className="bg-green-50/30 hover:bg-green-50 transition">
                <td className="p-4 border-b font-bold">Moisture Content</td>
                <td className="p-4 border-b">Less than 12%</td>
                <td className="p-4 border-b">Less than 10%</td>
              </tr>
              <tr className="hover:bg-green-50 transition">
                <td className="p-4 border-b font-bold">Crude Fiber</td>
                <td className="p-4 border-b">30% - 34%</td>
                <td className="p-4 border-b">25% - 30%</td>
              </tr>
              <tr className="bg-green-50/30 hover:bg-green-50 transition">
                <td className="p-4 border-b font-bold">Aflatoxin</td>
                <td className="p-4 border-b">Free / Negative</td>
                <td className="p-4 border-b">Free / Negative</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-center text-xs text-green-600 mt-4 italic">*Laboratory analysis certificates (SGS) available upon request for every shipment.</p>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="max-w-4xl mx-auto mb-10 px-6">
        <h3 className="text-3xl font-extrabold text-green-900 mb-8 text-center">Frequently Asked Questions</h3>
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-green-100">
            <h4 className="font-bold text-green-900">What is the Minimum Order Quantity (MOQ)?</h4>
            <p className="text-green-700 text-sm mt-2">Our standard MOQ is one 40ft High Cube (HC) container, which typically holds 18-22 tons depending on bale compression.</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-green-100">
            <h4 className="font-bold text-green-900">How long does shipping take to UAE or KSA?</h4>
            <p className="text-green-700 text-sm mt-2">Sea transit usually takes 7-12 days to Jebel Ali (UAE) and 12-15 days to Jeddah/Dammam (KSA) after port clearance.</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-green-100">
            <h4 className="font-bold text-green-900">Do you provide Phytosanitary Certificates?</h4>
            <p className="text-green-700 text-sm mt-2">Yes, every export shipment is accompanied by a government-issued Phytosanitary Certificate and SGS Quality Inspection report.</p>
          </div>
        </div>
      </section>

      {/* Engagement / Blog Section */}
      <section id="guide" className="max-w-7xl mx-auto mb-10 px-6">
        <div className="bg-emerald-900 text-white p-10 rounded-3xl shadow-2xl relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-extrabold mb-6 leading-tight">
              Are you looking for a reliable and professional Rhodes Grass Exporter?
            </h3>
            <p className="text-emerald-100 text-lg mb-8 max-w-3xl">
              Finding a trustworthy partner in the animal feed industry can be challenging. You need someone who understands moisture levels, protein requirements, and the strict customs regulations of the Gulf region.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="bg-white/10 p-5 rounded-xl backdrop-blur-sm border border-white/20">
                <h4 className="font-bold text-xl mb-2 text-amber-400">Quality Assured</h4>
                <p className="text-sm">We provide pre-shipment inspections and SGS certificates to ensure every bale meets your specific livestock needs.</p>
              </div>
              <div className="bg-white/10 p-5 rounded-xl backdrop-blur-sm border border-white/20">
                <h4 className="font-bold text-xl mb-2 text-amber-400">Reliable Shipping</h4>
                <p className="text-sm">Whether it's Jebel Ali, Riyadh, or Doha, we have a proven track record of on-time delivery without damage.</p>
              </div>
              <div className="bg-white/10 p-5 rounded-xl backdrop-blur-sm border border-white/20">
                <h4 className="font-bold text-xl mb-2 text-amber-400">Direct Support</h4>
                <p className="text-sm">No automated bots. You talk directly to our export managers who understand the GCC market dynamics.</p>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <a href="https://wa.me/971566229072" className="px-8 py-4 bg-amber-400 text-green-950 rounded-full font-black text-lg shadow-xl hover:bg-amber-500 transition-all">Yes, I want a Reliable Supplier</a>
              <button onClick={() => openQuote('Reliability Inquiry')} className="px-8 py-4 border-2 border-white rounded-full font-bold text-lg hover:bg-white hover:text-green-950 transition-all">Ask a Question</button>
            </div>
          </div>
          
          {/* Decorative Background Element */}
          <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-green-500/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6 mb-10">
        <div className="bg-white p-6 rounded-2xl shadow">
          <p className="text-green-800">“Great quality and timely shipment — highly recommended!”</p>
          <div className="mt-4 text-sm text-gray-600">— Ahmed, UAE</div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow">
          <p className="text-green-800">“Excellent communication and export docs — smooth process.”</p>
          <div className="mt-4 text-sm text-gray-600">— Fatima, Oman</div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow">
          <p className="text-green-800">“Good price and consistent quality.”</p>
          <div className="mt-4 text-sm text-gray-600">— Khalid, Qatar</div>
        </div>
      </section>

      {/* CTA / Newsletter */}
      <section className="max-w-7xl mx-auto bg-green-900 text-white rounded-2xl p-6 mb-10">
        <div className="md:flex md:justify-between md:items-center">
          <div>
            <h4 className="text-2xl font-extrabold">Get Export-Ready Quotes — Fast</h4>
            <p className="mt-2 text-sm text-green-200">Send us your email address if you are interested to buy we will contact you within 24 hours.</p>
          </div>
          <form onSubmit={subscribeNewsletter} className="mt-4 md:mt-0 md:flex gap-2">
            <input name="email" type="email" placeholder="Your email for quote & updates" required className="p-3 rounded-lg text-gray-800" />
            <button type="submit" className="px-4 py-3 bg-amber-400 text-green-900 rounded-lg font-bold">Send</button>
          </form>
        </div>
        {newsletterMsg && <div className="mt-3 text-sm">{newsletterMsg}</div>}
      </section>

      {/* Footer */}
      <footer id="contact" className="mt-12 border-t py-10 bg-green-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-10 mb-10 text-sm">
            <div>
              <h4 className="font-bold text-lg mb-4 text-amber-400">Our Reach</h4>
              <ul className="space-y-2 text-green-100">
                <li>• Rhodes Grass Export to **Jebel Ali, UAE**</li>
                <li>• Premium Fodder for **Riyadh & Jeddah, KSA**</li>
                <li>• Alfalfa Hay Supplier for **Doha Port, Qatar**</li>
                <li>• Animal Feed Logistics to **Sohar & Muscat, Oman**</li>
                <li>• Bulk Hay Shipping to **Khalifa Bin Salman Port, Bahrain**</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4 text-amber-400">Products Range</h4>
              <ul className="space-y-2 text-green-100">
                <li>• Export Quality Rhodes Grass (Chloris gayana)</li>
                <li>• Grade-A Alfalfa Hay (Lucerne)</li>
                <li>• High Protein Sudan Grass</li>
                <li>• Compressed Wheat Straw Bales</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4 text-amber-400">Contact Details</h4>
              <p className="text-green-100 mb-2">📍 Serving GCC, Middle East & Africa</p>
              <p className="text-green-100 mb-2">📞 Phone/WhatsApp: +971566229072</p>
              <p className="text-green-100">✉️ Email: najmaalkauthar@gmail.com</p>
            </div>
          </div>
          <div className="border-t border-green-800 pt-6 text-center text-xs text-green-300">
            © {new Date().getFullYear()} Najma Al Khautar — Professional Rhodes Grass Exporter. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Quote Modal (Formspree) */}
      {quoteOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-11/12 md:w-1/2 relative">
            <button onClick={closeQuote} className="absolute top-3 right-3 text-gray-600 hover:text-red-500 text-lg font-bold">✕</button>
            <h3 className="text-2xl font-bold text-green-900 mb-4">
              {quoteProduct === '2026 Price List' ? '📄 Download 2026 Price List & Lab Reports' : `Request a Quote — ${quoteProduct}`}
            </h3>
            <p className="text-xs text-green-700 mb-4">
              {quoteProduct === '2026 Price List' 
                ? 'Please provide your details to receive our latest high-protein analysis (SGS) and wholesale pricing.' 
                : 'Fill in the form below and our export manager will contact you with a custom quote.'}
            </p>
            <form action="https://formspree.io/f/xdkwebkn" method="POST" className="space-y-3">
              <input type="hidden" name="product" value={quoteProduct} />
              <input name="company" type="text" placeholder="Company Name (Required for Bulk Rates)" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none" required />
              <input name="name" type="text" placeholder="Your Full Name" className="w-full p-3 border rounded-lg" required />
              <input name="email" type="email" placeholder="Email Address (To send the PDF)" className="w-full p-3 border rounded-lg" required />
              <input name="phone" type="text" placeholder="WhatsApp Number (For fast delivery)" className="w-full p-3 border rounded-lg" required />
              <textarea name="message" placeholder="Your Message" className="w-full p-3 border rounded-lg" rows="4"></textarea>
              <button type="submit" className="w-full py-3 bg-green-700 text-white rounded-lg font-bold hover:bg-green-800 transition-all duration-300">Send Request</button>
            </form>
          </div>
        </div>
      )}

      {/* Styling for slider and animations */}
      <style>{`
        html { scroll-behavior: smooth }
        @keyframes slide { 0% { transform: translateX(0%) } 33% { transform: translateX(-33.333%) } 66% { transform: translateX(-66.666%) } 100% { transform: translateX(0%) } }
        .animate-slide { animation: slide 14s linear infinite; }
        .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border-width: 0; }
      `}</style>
    </div>
  );
}
